import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ShelfType } from 'src/app/models/layout/shelf-type';
import { ZoomService } from 'src/app/services/zoom.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { LayoutService } from 'src/app/services/layout.service';
import { PlanogramService } from 'src/app/services/planogram.service';
import { PlanogramShelf } from 'src/app/models/layout/planogram-shelf';
import { ShelfSettings } from 'src/app/models/shelf-settings';
import { PlanogramProductStack } from 'src/app/models/layout/planogram-product-stack';
import { DragContextService } from 'src/app/services/dragcontext.service';
import { PlaceholderService } from 'src/app/services/placeholder.service';
import { CapabilitiesService } from 'src/app/services/capabilities.service';
import { CapabilityType } from 'src/app/models/capability-type';
import { NgIf, AsyncPipe } from '@angular/common';
import { PlanogramManualProductComponent } from '../planogram-manual-product/planogram-manual-product.component';
import { PlanogramManualProductStackComponent } from '../planogram-manual-product-stack/planogram-manual-product-stack.component';

@Component({
    selector: 'app-planogram-manual-shelf',
    templateUrl: './planogram-manual-shelf.component.html',
    styleUrls: ['./planogram-manual-shelf.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, PlanogramManualProductComponent, PlanogramManualProductStackComponent, AsyncPipe]
})
export class PlanogramManualShelfComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('shelfContainer', { static: false }) shelfContainer!: ElementRef;
  @Input() shelf: PlanogramShelf | null = null;
  @Input() shelfIndex: number = -1;
  @Input() dropIndex: number = -1;
  @Input() shelfNumber: number = -1;
  @Input() shelfSettings: ShelfSettings | undefined;
  @Input() dropLipHeight: number | null | undefined = null;
  shelfId: string = '';
  shelfStyle$ = new BehaviorSubject<any>({});
  /** Cached filtered stacks. Recomputed in ngOnChanges when shelf input
   *  changes — using a getter allocated a new filtered array per CD cycle,
   *  forcing @for to diff a fresh reference even when nothing changed. */
  displayStacks: PlanogramProductStack[] = [];
  private subscriptions: Subscription[] = [];

  constructor(
    private zoomService: ZoomService,
    private layoutService: LayoutService,
    private planogramService: PlanogramService,
    private dragContextService: DragContextService,
    private capabilitiesService: CapabilitiesService,
    public placeholderService: PlaceholderService
  ) {
    this.subscriptions.push(
      this.zoomService.zoom$.subscribe(() => this.updateShelfStyle())
    );
    this.subscriptions.push(
      this.planogramService.shelfSettings$.subscribe(() => {
        this.updateShelfStyle();
      })
    );
    this.subscriptions.push(
      this.layoutService.layout$.subscribe(() => this.updateShelfStyle())
    );
  }

  ngOnInit() {
    this.shelfId = `drop-${this.dropIndex}-shelf-${this.shelfIndex}`;
    this.updateShelfStyle();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges) {
    // Force style update if dropLipHeight or shelf properties change via Inputs
    if (changes['dropLipHeight'] || changes['shelf']) {
      this.updateShelfStyle();
    }
    if (changes['shelf']) {
      this.displayStacks = this.shelf?.stacks?.filter((s) => !s.isPlaceholder) ?? [];
    }
  }

  get emptyShelfText(): string | null {
    return this.shelfSettings?.text &&
      (!this.shelf?.stacks || this.shelf.stacks.length === 0)
      ? this.shelfSettings.text
      : null;
  }

  onDragOver(event: MouseEvent) {
    if (!this.dragContextService.isDraggingProduct()) return;
    {{ if (!this.capabilitiesService.hasCapability(CapabilityType.EDIT_PLANOGRAM)) return; }}

    const dragContext = this.dragContextService.getContext();

    // Check if a stack component already handled this as a product placement
    const currentPlaceholder = this.placeholderService.getCurrentPlaceholder();
    if (currentPlaceholder.type === 'product') {
      // Stack already handled it, don't override
      return;
    }

    // Handle as stack placement
    const position = this.calculateStackPosition(event);
    const product = dragContext?.draggedProduct;

    if (product) {
      this.placeholderService.setStackPlaceholder(
        this.dropIndex,
        this.shelfIndex,
        position,
        product
      );
    }
  }

  onDragLeave(event: MouseEvent) {
    event.stopPropagation();
    if (!this.dragContextService.isDraggingProduct()) return;
    {{ if (!this.capabilitiesService.hasCapability(CapabilityType.EDIT_PLANOGRAM)) return; }}

    const relatedTarget = event.relatedTarget as HTMLElement;
    const currentTarget = event.currentTarget as HTMLElement;
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      this.placeholderService.clearPlaceholder();
    }
  }

  private calculateStackPosition(event: MouseEvent): number {
    const shelfElement = event.currentTarget as HTMLElement;
    const stacksContainer = shelfElement.querySelector(
      '.products-container'
    ) as HTMLElement;

    if (!stacksContainer) return 0;

    const stackElements = Array.from(
      stacksContainer.querySelectorAll('app-planogram-manual-product-stack')
    ) as HTMLElement[];

    if (stackElements.length === 0) return 0;

    const mouseX = event.clientX;

    // Check each gap between stacks
    for (let i = 0; i < stackElements.length; i++) {
      const stackRect = stackElements[i].getBoundingClientRect();

      // If we're before the left edge of this stack, insert before it
      if (mouseX < stackRect.left + stackRect.width * 0.25) {
        return i;
      }

      // If we're in the right portion of this stack and there's a next stack
      if (i < stackElements.length - 1) {
        const nextStackRect = stackElements[i + 1].getBoundingClientRect();
        const gapStart = stackRect.right - stackRect.width * 0.25;
        const gapEnd = nextStackRect.left + nextStackRect.width * 0.25;

        if (mouseX >= gapStart && mouseX <= gapEnd) {
          return i + 1; // Insert between current and next stack
        }
      }
    }

    // If we're after the last stack
    const lastStack = stackElements[stackElements.length - 1];
    const lastStackRect = lastStack.getBoundingClientRect();
    if (mouseX > lastStackRect.right - lastStackRect.width * 0.25) {
      return stackElements.length;
    }

    return 0; // Default fallback
  }

  updateShelfStyle() {
    let shelfStyle: any = {};
    let minHeight = this.shelf?.minHeight ?? 0;
    if (minHeight > 0) {
      shelfStyle['min-height'] = `${
        minHeight * this.zoomService.getCurrentZoom()
      }px`;
    }

    let baseLipHeight =
      this.dropLipHeight ?? this.planogramService.getShelfLipHeight();
    let shelfLipHeight = baseLipHeight * this.zoomService.getCurrentZoom();

    if (this.shelf?.shelfType == ShelfType.HANGCELL) {
      shelfStyle['border-top'] = `${shelfLipHeight}px solid #111`;
    } else {
      shelfStyle['border-bottom'] = `${shelfLipHeight}px solid #111`;
    }
    this.shelfStyle$.next(shelfStyle);
  }
}
